import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  ButtonBase,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import RemoveIcon from '@mui/icons-material/Remove';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { publicTableMenuApi } from '../utils/tableQrApi';
import { resolveSalonBrandingAssetUrl } from '../utils/salonBrandingApi';
import {
  calculateSelectedOptionsTotal,
  resolveDisplayOptionPrice,
  resolveSelectedOptionsForProduct,
  resolveCatalogImageUrl,
} from '../utils/catalog';

const GUEST_STORAGE_KEY = 'brutos.tableMenu.guest.v1';

const formatCurrency = (value) => Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const readQrToken = () => {
  if (typeof window === 'undefined') return '';
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const tokenFromHash = String(params.get('token') || params.get('t') || '').trim();
  if (tokenFromHash) {
    window.history.replaceState(
      { ...(window.history.state || {}), tableQrToken: tokenFromHash },
      document.title,
      `${window.location.pathname}${window.location.search}`
    );
    return tokenFromHash;
  }
  return String(window.history.state?.tableQrToken || '').trim();
};

const clearQrTokenFromHistory = () => {
  if (typeof window === 'undefined') return;
  const state = { ...(window.history.state || {}) };
  delete state.tableQrToken;
  window.history.replaceState(state, document.title, `${window.location.pathname}${window.location.search}`);
};

const readStoredGuest = () => {
  try {
    return JSON.parse(window.sessionStorage.getItem(GUEST_STORAGE_KEY) || 'null');
  } catch {
    return null;
  }
};

const writeStoredGuest = (guest) => {
  if (!guest) window.sessionStorage.removeItem(GUEST_STORAGE_KEY);
  else window.sessionStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(guest));
};

export const isStoredTableGuestExpired = (guest, now = Date.now()) => {
  if (!guest?.expiresAt) return false;
  const expiresAt = Date.parse(guest.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= now;
};

const productId = (product) => product.id || product.productId || product.dbId || product.itemId || product.externalCode;
const productName = (product) => product.name || product.itemName || 'Item';
const productDescription = (product) => product.description || product.itemDescription || '';
const productPrice = (product) => Number(product.price?.value ?? product.price ?? product.unitPrice ?? product.itemPrice?.value ?? 0);
const productImage = (product) => resolveCatalogImageUrl(
  product?.imageUrl || product?.image || product?.logoUrl || product?.payload?.imageUrl || ''
) || '';
const productCategory = (product) => product.categoryName || product.category || product.categoryLabel || 'Outros';
const productGroups = (product) => product.optionGroups || product.itemOptionGroups || product.payload?.optionGroups || [];
const groupId = (group) => group.id || group.groupId || group.optionGroupId || group.name;
const optionId = (option) => option.id || option.optionId || option.productId || option.externalCode || option.name;
const optionName = (option = {}) => option?.name || option?.label || 'Opção';
const optionDescription = (option = {}) => String(option?.description || '').trim();
const optionImage = (option = {}) => resolveCatalogImageUrl(
  String(option?.imageUrl || option?.image || option?.imagePath || '').trim()
) || '';
const optionHasDetails = (option = {}) => Boolean(optionDescription(option) || optionImage(option));

export const PublicProductDialogImage = ({ product }) => {
  const imageUrl = product ? productImage(product) : '';
  if (!imageUrl) return null;

  return (
    <Box
      component="img"
      src={imageUrl}
      alt={`Imagem de ${productName(product)}`}
      sx={{
        display: 'block',
        width: '100%',
        height: { xs: 170, sm: 230 },
        objectFit: 'cover',
        objectPosition: 'center',
        borderRadius: 2.5,
        bgcolor: 'grey.100',
      }}
    />
  );
};

export const PublicProductCard = ({ product, onOpen }) => (
  <Card
    variant="outlined"
    sx={{
      display: 'flex',
      alignItems: 'stretch',
      borderRadius: 3,
      overflow: 'hidden',
    }}
  >
    <CardActionArea
      aria-label={`Ver detalhes de ${productName(product)}`}
      onClick={() => onOpen(product)}
      sx={{ display: 'flex', alignItems: 'stretch', flex: 1, minWidth: 0, textAlign: 'left' }}
    >
      {productImage(product) ? (
        <CardMedia
          component="img"
          image={productImage(product)}
          alt={`Imagem de ${productName(product)}`}
          sx={{
            width: { xs: 112, sm: 148 },
            minWidth: { xs: 112, sm: 148 },
            aspectRatio: '4 / 3',
            height: { xs: 84, sm: 111 },
            alignSelf: 'flex-start',
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      ) : null}
      <CardContent sx={{ flex: 1, minWidth: 0 }}>
        <Typography fontWeight={900}>{productName(product)}</Typography>
        {productDescription(product) ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.5,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              overflow: 'hidden',
            }}
          >
            {productDescription(product)}
          </Typography>
        ) : null}
        <Typography color="primary" fontWeight={950} sx={{ mt: 1 }}>{formatCurrency(productPrice(product))}</Typography>
      </CardContent>
    </CardActionArea>

    <ButtonBase
      aria-label={`Adicionar ${productName(product)}`}
      onClick={() => onOpen(product)}
      sx={{
        alignSelf: 'center',
        flex: '0 0 auto',
        flexDirection: 'column',
        gap: 0.5,
        minWidth: { xs: 64, sm: 88 },
        mx: { xs: 0.75, sm: 1.25 },
        px: { xs: 0.5, sm: 1 },
        py: 1,
        borderRadius: 2.5,
        color: '#d71920',
        transition: 'background-color 160ms ease, transform 160ms ease',
        '&:hover': {
          bgcolor: '#fff1f1',
          transform: 'translateY(-1px)',
        },
        '&:focus-visible': {
          outline: '3px solid rgba(215, 25, 32, 0.24)',
          outlineOffset: 2,
        },
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
          '&:hover': { transform: 'none' },
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 40, sm: 44 },
          height: { xs: 40, sm: 44 },
          display: 'grid',
          placeItems: 'center',
          borderRadius: '50%',
          bgcolor: '#e9242e',
          color: '#fff',
          boxShadow: '0 6px 14px rgba(215, 25, 32, 0.28)',
        }}
      >
        <AddIcon sx={{ fontSize: { xs: 25, sm: 27 } }} />
      </Box>
      <Typography
        component="span"
        variant="caption"
        fontWeight={900}
        sx={{ fontSize: { xs: '0.68rem', sm: '0.75rem' }, lineHeight: 1.1 }}
      >
        Adicionar
      </Typography>
    </ButtonBase>
  </Card>
);

export const PublicOptionSelectorRow = ({ option, selected, displayPrice, onDecrease, onIncrease, onOpenDetails }) => {
  const name = optionName(option);
  const description = optionDescription(option);
  const imageUrl = optionImage(option);
  const hasDetails = optionHasDetails(option);
  const content = (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, width: '100%' }}>
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={`Foto de ${name}`}
          sx={{
            width: { xs: 48, sm: 56 },
            height: { xs: 48, sm: 56 },
            flex: '0 0 auto',
            objectFit: 'cover',
            objectPosition: 'center',
            borderRadius: 1.5,
            bgcolor: 'grey.100',
          }}
        />
      ) : null}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography color="text.primary" fontWeight={750}>{name}</Typography>
        {description ? (
          <Typography
            variant="body2"
            color="text.secondary"
            title={description}
            sx={{
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: { xs: '0.82rem', sm: '0.875rem' },
            }}
          >
            {description}
          </Typography>
        ) : null}
        {displayPrice ? (
          <Typography variant="body2" color="primary">
            + {formatCurrency(displayPrice)}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );

  return (
    <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" gap={{ xs: 0.5, sm: 1 }}>
        {hasDetails ? (
          <ButtonBase
            aria-label={`Ver detalhes de ${name}`}
            onClick={() => onOpenDetails(option)}
            sx={{
              flex: 1,
              minWidth: 0,
              justifyContent: 'flex-start',
              textAlign: 'left',
              borderRadius: 1.5,
              p: 0.25,
              mx: -0.25,
              '&:focus-visible': {
                outline: '3px solid rgba(25, 118, 210, 0.24)',
                outlineOffset: 2,
              },
            }}
          >
            {content}
          </ButtonBase>
        ) : (
          <Box sx={{ flex: 1, minWidth: 0 }}>{content}</Box>
        )}
        <IconButton size="small" aria-label={`Remover ${name}`} onClick={onDecrease} disabled={!selected}><RemoveIcon /></IconButton>
        <Typography minWidth={20} textAlign="center" fontWeight={900}>{selected?.quantity || 0}</Typography>
        <IconButton size="small" color="primary" aria-label={`Adicionar ${name}`} onClick={onIncrease}><AddIcon /></IconButton>
      </Stack>
    </Paper>
  );
};

export const PublicOptionDetailsDialog = ({ option, onClose }) => {
  const name = optionName(option);
  const description = optionDescription(option);
  const imageUrl = optionImage(option);

  return (
    <Dialog open={Boolean(option)} onClose={onClose} fullWidth maxWidth="xs" aria-describedby={description ? 'public-option-description' : undefined}>
      <DialogTitle sx={{ pr: 6, color: 'text.primary', fontWeight: 900 }}>
        {option ? name : ''}
        <IconButton aria-label="Fechar detalhes do complemento" onClick={onClose} sx={{ position: 'absolute', right: 10, top: 10 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {imageUrl ? (
            <Box
              component="img"
              src={imageUrl}
              alt={`Foto de ${name}`}
              sx={{
                display: 'block',
                width: '100%',
                height: { xs: 190, sm: 240 },
                objectFit: 'contain',
                objectPosition: 'center',
                borderRadius: 2,
                bgcolor: 'grey.100',
              }}
            />
          ) : null}
          {description ? (
            <Typography id="public-option-description" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
              {description}
            </Typography>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

const normalizeCatalog = (payload = {}) => {
  const catalog = payload.catalog || payload;
  const products = catalog.products || catalog.items || [];
  const explicitCategories = catalog.categories || [];
  const categoryNames = explicitCategories.map((category) => (
    typeof category === 'string' ? category : category.name || category.label
  )).filter(Boolean);
  products.forEach((product) => {
    const category = productCategory(product);
    if (category && !categoryNames.includes(category)) categoryNames.push(category);
  });
  return {
    products,
    categories: categoryNames,
    branding: payload.branding || catalog.branding || null,
  };
};

const asPricingProduct = (product = {}) => ({
  ...product,
  itemOptionGroups: productGroups(product),
});

const resolvedSelectedOptions = (product = {}, selected = {}) => (
  resolveSelectedOptionsForProduct(asPricingProduct(product), selected)
);

const optionSelectionTotal = (product = {}, selected = {}) => (
  calculateSelectedOptionsTotal(asPricingProduct(product), resolvedSelectedOptions(product, selected))
);

const newIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `table-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const PublicTableMenu = () => {
  const [{ qrToken, storedGuest }] = useState(() => {
    const nextQrToken = readQrToken();
    if (nextQrToken) writeStoredGuest(null);
    const savedGuest = nextQrToken ? null : readStoredGuest();
    const validGuest = isStoredTableGuestExpired(savedGuest) ? null : savedGuest;
    if (savedGuest && !validGuest) writeStoredGuest(null);
    return { qrToken: nextQrToken, storedGuest: validGuest };
  });
  const [context, setContext] = useState(storedGuest?.context || null);
  const [guest, setGuest] = useState(storedGuest || null);
  const [customerName, setCustomerName] = useState(storedGuest?.customerName || '');
  const [initialLoading, setInitialLoading] = useState(!storedGuest?.guestToken);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState('');
  const [catalog, setCatalog] = useState({ products: [], categories: [], branding: null });
  const [catalogLoading, setCatalogLoading] = useState(Boolean(storedGuest?.guestToken));
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Todos');
  const [itemDialog, setItemDialog] = useState(null);
  const [itemQuantity, setItemQuantity] = useState(1);
  const [observations, setObservations] = useState('');
  const [selectedOptions, setSelectedOptions] = useState({});
  const [optionDetail, setOptionDetail] = useState(null);
  const [itemError, setItemError] = useState('');
  const [cart, setCart] = useState([]);
  const [checkoutKey, setCheckoutKey] = useState(newIdempotencyKey);
  const [cartOpen, setCartOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const invalidateGuest = useCallback((message) => {
    writeStoredGuest(null);
    setGuest(null);
    setContext(null);
    setCatalog({ products: [], categories: [], branding: null });
    setCart([]);
    setCartOpen(false);
    setItemDialog(null);
    setError(message || 'Este acesso expirou. Escaneie novamente o QR Code da mesa.');
  }, []);

  useEffect(() => {
    if (!guest?.expiresAt) return undefined;
    const expiresAt = Date.parse(guest.expiresAt);
    if (!Number.isFinite(expiresAt)) return undefined;
    const remainingMs = expiresAt - Date.now();
    if (remainingMs <= 0) {
      invalidateGuest('Este acesso de 1 hora expirou. Escaneie novamente o QR Code da mesa.');
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      invalidateGuest('Este acesso de 1 hora expirou. Escaneie novamente o QR Code da mesa.');
    }, Math.min(remainingMs, 2147483647));
    return () => window.clearTimeout(timeoutId);
  }, [guest?.expiresAt, invalidateGuest]);

  const loadCatalog = useCallback(async (guestToken) => {
    setCatalogLoading(true);
    try {
      const data = await publicTableMenuApi.catalog(guestToken);
      setCatalog(normalizeCatalog(data));
      setError('');
    } catch (err) {
      if ([401, 403, 409, 410].includes(err.status)) invalidateGuest(err.message);
      else setError(err.message);
    } finally {
      setCatalogLoading(false);
    }
  }, [invalidateGuest]);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      if (storedGuest?.guestToken) {
        setInitialLoading(false);
        await loadCatalog(storedGuest.guestToken);
        return;
      }
      if (!qrToken) {
        setError('QR Code não encontrado. Escaneie novamente a placa disponível na mesa.');
        setInitialLoading(false);
        return;
      }
      try {
        const data = await publicTableMenuApi.context(qrToken);
        if (!active) return;
        setContext(data.context || data);
      } catch (err) {
        if (active) setError(err.message);
      } finally {
        if (active) setInitialLoading(false);
      }
    };
    initialize();
    return () => { active = false; };
  }, [loadCatalog, qrToken, storedGuest]);

  const joinTable = async () => {
    const name = customerName.trim();
    if (name.length < 2) {
      setError('Informe seu nome para continuar.');
      return;
    }
    if (!qrToken) {
      setError('Escaneie novamente o QR Code da mesa.');
      return;
    }
    setJoining(true);
    setError('');
    try {
      const data = await publicTableMenuApi.join(qrToken, name);
      const nextGuest = {
        guestToken: data.guestToken,
        expiresAt: data.expiresAt,
        customerName: name,
        context: {
          ...context,
          table: data.table || context?.table,
          session: data.session || context?.session,
        },
      };
      if (!nextGuest.guestToken) throw new Error('Sessão da mesa não foi criada.');
      setGuest(nextGuest);
      setContext(nextGuest.context);
      writeStoredGuest(nextGuest);
      clearQrTokenFromHistory();
      await loadCatalog(nextGuest.guestToken);
    } catch (err) {
      setError(err.message);
    } finally {
      setJoining(false);
    }
  };

  const visibleProducts = useMemo(() => catalog.products.filter((product) => {
    const byCategory = category === 'Todos' || productCategory(product) === category;
    const query = search.trim().toLocaleLowerCase('pt-BR');
    const bySearch = !query || `${productName(product)} ${productDescription(product)}`.toLocaleLowerCase('pt-BR').includes(query);
    return byCategory && bySearch;
  }), [catalog.products, category, search]);

  const openProduct = (product) => {
    setItemDialog(product);
    setItemQuantity(1);
    setObservations('');
    setSelectedOptions({});
    setItemError('');
    setOptionDetail(null);
  };

  const closeProduct = () => {
    setOptionDetail(null);
    setItemDialog(null);
  };

  const changeOption = (group, option, delta) => {
    const groupKey = String(groupId(group));
    const optionKey = String(optionId(option));
    const max = Number(group.maxQuantity ?? group.max ?? 0);
    setSelectedOptions((previous) => {
      const next = { ...previous, [groupKey]: { ...(previous[groupKey] || {}) } };
      const current = Number(next[groupKey][optionKey]?.quantity || 0);
      const selectedInGroup = Object.values(next[groupKey]).reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      if (delta > 0 && max > 0 && selectedInGroup >= max) return previous;
      const quantity = Math.max(0, current + delta);
      if (!quantity) delete next[groupKey][optionKey];
      else next[groupKey][optionKey] = { ...option, quantity };
      return next;
    });
  };

  const addToCart = () => {
    if (cart.length >= 50) {
      setItemError('O pedido aceita no máximo 50 itens diferentes por envio. Envie este pedido antes de adicionar mais.');
      return;
    }
    for (const group of productGroups(itemDialog)) {
      const groupKey = String(groupId(group));
      const count = Object.values(selectedOptions[groupKey] || {}).reduce((sum, option) => sum + Number(option.quantity || 0), 0);
      const min = Number(group.minQuantity ?? group.min ?? (group.required ? 1 : 0));
      const max = Number(group.maxQuantity ?? group.max ?? 0);
      if (count < min) {
        setItemError(`Escolha pelo menos ${min} opção(ões) em ${group.name || 'complementos'}.`);
        return;
      }
      if (max > 0 && count > max) {
        setItemError(`Escolha no máximo ${max} opção(ões) em ${group.name || 'complementos'}.`);
        return;
      }
    }
    const options = resolvedSelectedOptions(itemDialog, selectedOptions).map((option) => ({
      groupId: option.groupId,
      groupName: option.groupName || '',
      optionId: optionId(option),
      name: option.name || option.label || 'Opção',
      quantity: Number(option.quantity || 1),
      price: Number(option.price || 0),
    }));
    setCart((previous) => [...previous, {
      localId: newIdempotencyKey(),
      productId: productId(itemDialog),
      name: productName(itemDialog),
      quantity: itemQuantity,
      observations: observations.trim(),
      options,
      unitPrice: productPrice(itemDialog) + optionSelectionTotal(itemDialog, selectedOptions),
    }]);
    setCheckoutKey(newIdempotencyKey());
    setItemDialog(null);
    setToast({ open: true, message: 'Item adicionado ao pedido.', severity: 'success' });
  };

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);

  const sendOrder = async () => {
    if (!guest?.guestToken || !cart.length) return;
    setSubmitting(true);
    setError('');
    try {
      const data = await publicTableMenuApi.createOrder(guest.guestToken, {
        idempotencyKey: checkoutKey,
        items: cart.map((item) => ({
          productId: item.productId,
          quantity: item.quantity,
          observations: item.observations,
          options: item.options.map((option) => ({
            groupId: option.groupId,
            optionId: option.optionId,
            quantity: option.quantity,
          })),
        })),
      });
      setCart([]);
      setCheckoutKey(newIdempotencyKey());
      setCartOpen(false);
      setToast({
        open: true,
        message: `Pedido ${data.order?.displayId || data.displayId || ''} enviado para a cozinha!`.replace(/\s+/g, ' ').trim(),
        severity: 'success',
      });
    } catch (err) {
      if ([401, 403, 409, 410].includes(err.status)) invalidateGuest(err.message);
      else setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const table = context?.table || context || {};
  const tableNumber = table.number || table.tableNumber || context?.tableNumber;
  const restaurantName = context?.restaurant?.name || context?.companyName || context?.restaurantName || 'Cardápio digital';
  const publicBranding = catalog.branding || context?.branding || {};
  const logoUrl = resolveSalonBrandingAssetUrl(publicBranding.logoUrl);
  const coverUrl = resolveSalonBrandingAssetUrl(publicBranding.coverUrl);

  if (initialLoading) {
    return (
      <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', bgcolor: '#f8fafc' }}>
        <Stack alignItems="center" spacing={2}><CircularProgress /><Typography>Identificando sua mesa...</Typography></Stack>
      </Box>
    );
  }

  if (!guest?.guestToken) {
    return (
      <Box sx={{ minHeight: '100dvh', bgcolor: '#f8fafc', display: 'grid', placeItems: 'center', p: 2 }}>
        <Paper elevation={0} sx={{ width: '100%', maxWidth: 440, borderRadius: 4, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
          {coverUrl ? (
            <Box
              role="img"
              aria-label={`Capa de ${restaurantName}`}
              sx={{
                height: { xs: 130, sm: 160 },
                bgcolor: '#1f2937',
                backgroundImage: `linear-gradient(180deg, rgba(15,23,42,0.05), rgba(15,23,42,0.45)), url("${coverUrl}")`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
              }}
            />
          ) : null}
          <Stack spacing={2.5} alignItems="center" sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Avatar
              src={logoUrl || undefined}
              alt={logoUrl ? `Logo de ${restaurantName}` : ''}
              sx={{
                width: 72,
                height: 72,
                bgcolor: logoUrl ? '#fff' : 'primary.main',
                mt: coverUrl ? { xs: -7, sm: -8.5 } : 0,
                border: logoUrl ? '3px solid #fff' : 'none',
                boxShadow: coverUrl ? '0 8px 22px rgba(15,23,42,0.2)' : 'none',
                '& img': { objectFit: 'contain' },
              }}
            >
              {!logoUrl ? <EventSeatIcon fontSize="large" /> : null}
            </Avatar>
            <Box textAlign="center">
              <Typography variant="overline" color="primary" fontWeight={900}>{restaurantName}</Typography>
              <Typography variant="h4" fontWeight={950}>Mesa {tableNumber || ''}</Typography>
              {table.name ? <Typography color="text.secondary">{table.name}</Typography> : null}
            </Box>
            {error ? <Alert severity="error" sx={{ width: '100%' }}>{error}</Alert> : null}
            {context ? (
              <>
                <Typography textAlign="center" color="text.secondary">
                  Digite apenas seu nome para abrir o cardápio. Outras pessoas nesta mesa também podem pedir pelo mesmo QR Code.
                </Typography>
                <TextField
                  autoFocus
                  fullWidth
                  label="Seu nome"
                  value={customerName}
                  inputProps={{ maxLength: 80, autoComplete: 'name' }}
                  onChange={(event) => setCustomerName(event.target.value)}
                  onKeyDown={(event) => { if (event.key === 'Enter') joinTable(); }}
                />
                <Button fullWidth size="large" variant="contained" disabled={joining || customerName.trim().length < 2} onClick={joinTable}>
                  {joining ? <CircularProgress size={24} color="inherit" /> : 'Ver cardápio'}
                </Button>
              </>
            ) : null}
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: '#f8fafc', pb: 12 }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#fff', color: 'text.primary', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Toolbar sx={{ gap: 1.5 }}>
          <Avatar
            src={logoUrl || undefined}
            alt={logoUrl ? `Logo de ${restaurantName}` : ''}
            sx={{ bgcolor: logoUrl ? '#fff' : 'primary.main', '& img': { objectFit: 'contain' } }}
          >
            {!logoUrl ? <RestaurantMenuIcon /> : null}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={950} noWrap>{restaurantName}</Typography>
            <Typography variant="caption" color="text.secondary">Mesa {tableNumber} • {guest.customerName}</Typography>
          </Box>
          <IconButton onClick={() => setCartOpen(true)} aria-label={`Abrir pedido com ${cartCount} itens`}>
            <Badge badgeContent={cartCount} color="primary"><ShoppingCartOutlinedIcon /></Badge>
          </IconButton>
        </Toolbar>
      </AppBar>

      {coverUrl ? (
        <Box
          role="img"
          aria-label={`Capa de ${restaurantName}`}
          sx={{
            height: { xs: 132, sm: 176 },
            bgcolor: '#1f2937',
            backgroundImage: `linear-gradient(180deg, rgba(15,23,42,0.04), rgba(15,23,42,0.34)), url("${coverUrl}")`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        />
      ) : null}

      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', px: 2, py: 2.5 }}>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
        <TextField
          fullWidth
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar no cardápio"
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
          sx={{ bgcolor: '#fff', mb: 2 }}
        />
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 1.5 }}>
          {['Todos', ...catalog.categories].map((name) => (
            <Chip key={name} label={name} clickable color={category === name ? 'primary' : 'default'} onClick={() => setCategory(name)} />
          ))}
        </Stack>

        {catalogLoading ? (
          <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}><CircularProgress /><Typography>Carregando cardápio...</Typography></Stack>
        ) : !visibleProducts.length ? (
          <Alert severity="info">Nenhum item disponível nesta categoria.</Alert>
        ) : (
          <Stack spacing={1.25}>
            {visibleProducts.map((product) => (
              <PublicProductCard key={productId(product)} product={product} onOpen={openProduct} />
            ))}
          </Stack>
        )}
      </Box>

      {cart.length ? (
        <Paper elevation={8} sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, p: 1.5, zIndex: 10, borderRadius: '18px 18px 0 0' }}>
          <Button fullWidth size="large" variant="contained" onClick={() => setCartOpen(true)} startIcon={<ShoppingCartOutlinedIcon />}>
            Ver pedido ({cartCount}) • {formatCurrency(cartTotal)}
          </Button>
        </Paper>
      ) : null}

      <Dialog open={Boolean(itemDialog)} onClose={closeProduct} fullWidth maxWidth="sm" fullScreen={false}>
        <DialogTitle sx={{ pr: 6 }}>
          {itemDialog ? productName(itemDialog) : ''}
          <IconButton aria-label="Fechar detalhes do produto" onClick={closeProduct} sx={{ position: 'absolute', right: 10, top: 10 }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <PublicProductDialogImage product={itemDialog} />
            {itemDialog && productDescription(itemDialog) ? <Typography color="text.secondary">{productDescription(itemDialog)}</Typography> : null}
            {itemError ? <Alert severity="error">{itemError}</Alert> : null}
            {itemDialog && productGroups(itemDialog).map((group) => {
              const groupKey = String(groupId(group));
              const min = Number(group.minQuantity ?? group.min ?? (group.required ? 1 : 0));
              const max = Number(group.maxQuantity ?? group.max ?? 0);
              return (
                <Box key={groupKey}>
                  <Typography fontWeight={950}>{group.name || 'Opções'}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {min > 0 ? `Escolha no mínimo ${min}` : 'Opcional'}{max > 0 ? ` • máximo ${max}` : ''}
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {(group.options || group.items || []).filter((option) => option.available !== false).map((option) => {
                      const selected = selectedOptions[groupKey]?.[String(optionId(option))];
                      const displayPrice = resolveDisplayOptionPrice(asPricingProduct(itemDialog), group, option, selectedOptions);
                      return (
                        <PublicOptionSelectorRow
                          key={optionId(option)}
                          option={option}
                          selected={selected}
                          displayPrice={displayPrice}
                          onDecrease={() => changeOption(group, option, -1)}
                          onIncrease={() => changeOption(group, option, 1)}
                          onOpenDetails={setOptionDetail}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
            <TextField label="Observações (opcional)" multiline minRows={2} value={observations} inputProps={{ maxLength: 300 }} onChange={(event) => setObservations(event.target.value)} />
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
              <IconButton onClick={() => setItemQuantity((value) => Math.max(1, value - 1))}><RemoveIcon /></IconButton>
              <Typography variant="h6" fontWeight={950}>{itemQuantity}</Typography>
              <IconButton color="primary" onClick={() => setItemQuantity((value) => Math.min(30, value + 1))}><AddIcon /></IconButton>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button fullWidth size="large" variant="contained" onClick={addToCart} disabled={cart.length >= 50}>
            {cart.length >= 50
              ? 'Limite de 50 itens atingido'
              : `Adicionar • ${formatCurrency(itemDialog ? (productPrice(itemDialog) + optionSelectionTotal(itemDialog, selectedOptions)) * itemQuantity : 0)}`}
          </Button>
        </DialogActions>
      </Dialog>

      <PublicOptionDetailsDialog option={optionDetail} onClose={() => setOptionDetail(null)} />

      <Dialog open={cartOpen} onClose={() => setCartOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Seu pedido</DialogTitle>
        <DialogContent>
          {!cart.length ? <Alert severity="info">Seu pedido ainda está vazio.</Alert> : (
            <Stack divider={<Divider flexItem />} spacing={1.5}>
              {cart.map((item) => (
                <Stack key={item.localId} direction="row" gap={1.5} sx={{ py: 1 }}>
                  <Typography fontWeight={950}>{item.quantity}x</Typography>
                  <Box sx={{ flex: 1 }}>
                    <Typography fontWeight={850}>{item.name}</Typography>
                    {item.options.length ? <Typography variant="caption" display="block">{item.options.map((option) => `${option.quantity}x ${option.name}`).join(', ')}</Typography> : null}
                    {item.observations ? <Typography variant="caption" display="block">Obs: {item.observations}</Typography> : null}
                    <Typography color="primary" fontWeight={900}>{formatCurrency(item.unitPrice * item.quantity)}</Typography>
                  </Box>
                  <IconButton
                    aria-label={`Remover ${item.name}`}
                    onClick={() => {
                      setCart((items) => items.filter((candidate) => candidate.localId !== item.localId));
                      setCheckoutKey(newIdempotencyKey());
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, display: 'block' }}>
          <Stack spacing={1}>
            <Stack direction="row" justifyContent="space-between"><Typography fontWeight={900}>Total estimado</Typography><Typography fontWeight={950}>{formatCurrency(cartTotal)}</Typography></Stack>
            <Button fullWidth size="large" variant="contained" disabled={!cart.length || submitting} onClick={sendOrder}>
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Enviar pedido para a cozinha'}
            </Button>
            <Typography variant="caption" color="text.secondary" textAlign="center">Os valores e a disponibilidade são confirmados pelo restaurante ao enviar.</Typography>
          </Stack>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={4500} onClose={() => setToast((value) => ({ ...value, open: false }))}>
        <Alert severity={toast.severity} variant="filled" onClose={() => setToast((value) => ({ ...value, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default PublicTableMenu;
